import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { workStagesAPI } from '../../services/workStagesApi'

const schema = yup.object({
  name: yup.string().required('Название обязательно'),
  foreman_discount_percentage: yup.number()
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .min(0, 'Минимум 0%')
    .max(100, 'Максимум 100%'),
}).required()

const WorkStageForm = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (isEdit) {
      loadWorkStage()
    }
  }, [id])

  const loadWorkStage = async () => {
    try {
      setLoading(true)
      const response = await workStagesAPI.get(id)
      reset({
        name: response.data.name,
        foreman_discount_percentage: response.data.foreman_discount_percentage || '',
      })
    } catch (err) {
      setError('Ошибка загрузки этапа работ')
      console.error('Error loading work stage:', err)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      setError(null)

      if (isEdit) {
        await workStagesAPI.update(id, data)
      } else {
        await workStagesAPI.create(data)
      }

      navigate('/work_stages')
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorMessages = Object.entries(err.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n')
        setError(errorMessages)
      } else {
        setError(isEdit ? 'Ошибка при обновлении этапа работ' : 'Ошибка при создании этапа работ')
      }
      console.error('Error saving work stage:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <h1 className="mb-4">
        {isEdit ? 'Редактировать этап работ' : 'Создать новый этап работ'}
      </h1>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <h5>Ошибки:</h5>
          <pre className="mb-0">{error}</pre>
        </Alert>
      )}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Form.Label>Название</Form.Label>
          <Form.Control
            type="text"
            {...register('name')}
            isInvalid={!!errors.name}
          />
          <Form.Control.Feedback type="invalid">
            {errors.name?.message}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Стоимость работы мастера (%)</Form.Label>
          <Form.Control
            type="number"
            step="0.01"
            min="0"
            max="100"
            placeholder="Введите процент (0-100) или оставьте пустым"
            {...register('foreman_discount_percentage')}
            isInvalid={!!errors.foreman_discount_percentage}
          />
          <Form.Control.Feedback type="invalid">
            {errors.foreman_discount_percentage?.message}
          </Form.Control.Feedback>
          <Form.Text className="text-muted">
            Процент стоимости работы мастера для этого этапа. Можно оставить пустым (будет 0%).
          </Form.Text>
        </Form.Group>

        <div className="mb-3">
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Сохранение...' : 'Сохранить'}
          </Button>
        </div>
      </Form>
    </div>
  )
}

export default WorkStageForm
